import React, { useState } from "react";
import { GLOBAL_IMAGES } from "../../assets/asset";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useUpdateUser } from "../../features/auth/useLogin";
import { useSelector } from "react-redux";
import useProfileAvatars from "../../features/queryHooks/useProfileAvatars";

const ProfileEdit = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const updateUserMutation = useUpdateUser();
  const profileAvatarQuery = useProfileAvatars();

  const [formData, setFormData] = useState({
    id: user.pk || user.UserId,
    user_name: user.user_name || "",
    parents_date_of_birth: user.parents_date_of_birth || "",
    profilephotos: user.profilephotos || "",
  });

  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleAvatarSelect = (avatarUrl) => {
    setFormData((p) => ({ ...p, profilephotos: avatarUrl }));
  };
  const showToast = (message, type) => {
    setToast({ show: true, message, type });

    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const updateUsername = () => {
    updateUserMutation.mutate(
      {
        id: formData.id,
        user_name: formData.user_name,
      },
      {
        onSuccess: () => showToast("Username updated successfully", "success"),
        onError: () => showToast("Failed to update username", "error"),
      }
    );
  };

  const updateAvatar = () => {
    updateUserMutation.mutate(
      {
        id: formData.id,
        profilephotos: formData.profilephotos,
      },
      {
        onSuccess: () => {
          showToast("Avatar updated successfully", "success");
        },
        onError: () => {
          showToast("Failed to update avatar", "error");
        },
        onSettled: () => {
          setShowAvatarModal(false);
        },
      }
    );
  };

  const closeAvatarModal = () => {
    setFormData((p) => ({ ...p, profilephotos: user.profilephotos }));
    setShowAvatarModal(false);
  };

  return (
    <div
      className="profile-edit-page settings-page min-h-screen flex flex-col items-center bg-blue-50 font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <header className="settings-header profile-edit-header relative w-full flex items-center px-20 pt-3">
        {/* Left side (logo + back) */}
        <div className="settings-header-left profile-edit-header-left flex items-center gap-14">
          <Link to={"/"}>
            <img src={GLOBAL_IMAGES.dingo} className="settings-header-logo h-20 w-auto" alt="" />
          </Link>
          <img
            src={GLOBAL_IMAGES.LoginBackButton}
            className="settings-header-back h-14 w-auto cursor-pointer"
            alt="Back"
            onClick={() => navigate(-1)}
          />
        </div>

        {/* Center title */}
      </header>
      {toast.show && (
        <div
          className={`profile-edit-toast fixed top-4 right-4 py-3 px-6 rounded-xl shadow-xl z-50 transition-all duration-300 text-lg transform scale-125 ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : toast.type === "error"
              ? "bg-red-500 text-white"
              : "bg-yellow-500 text-white"
          }`}
          style={{ transform: "scale(1.3)", transformOrigin: "top right" }}
        >
          {toast.message}
        </div>
      )}
      <div className="profile-edit-breadcrumb flex gap-2 mb-6 absolute left-12 top-28">
        <Link to={`/settings`} className="">
          Settings
        </Link>
        <ChevronRight className="" />
        <p className="text-gray-700">Profile</p>
      </div>

      <div className="profile-edit-card p-8 w-full max-w-lg text-center mt-[5%]">
        <img
          src={formData.profilephotos}
          alt="Profile"
          className="profile-edit-avatar w-24 h-24 rounded-full mx-auto mb-4 cursor-pointer"
          onClick={() => setShowAvatarModal(true)}
        />
        <p className="profile-edit-email text-xl font-semibold text-gray-800">{user.email}</p>
        <div className="profile-edit-form space-y-4">
          <div>
            <label
              htmlFor="username"
              className="profile-edit-label text-lg text-gray-800 font-semibold block mb-2 text-start mt-10"
            >
              User name
            </label>
            <input
              id="username"
              name="user_name"
              value={formData.user_name}
              onChange={handleChange}
              className="profile-edit-input w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter new username"
            />
            <button
              onClick={updateUsername}
              disabled={updateUserMutation.isPending}
              className="profile-edit-submit bg-[#06059B] text-white px-6 py-3 rounded-xl font-semibold w-[70%] mt-4 relative"
            >
              {updateUserMutation.isPending ? (
                <>
                  <span className="opacity-0">Update</span>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                </>
              ) : (
                "Update "
              )}
            </button>
          </div>
        </div>
      </div>
      {showAvatarModal && (
        <div className="profile-avatar-modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="profile-avatar-modal bg-white p-6 rounded-xl w-3/4 max-w-md">
            <h2 className="profile-avatar-modal-title text-2xl font-semibold mb-4">Choose an Avatar:</h2>
            <div className="flex">
              {profileAvatarQuery.isPending || !profileAvatarQuery.data.data ? (
                <div className="profile-avatar-grid w-full grid grid-cols-5 gap-4">
                  {Array.from({ length: 25 }, (_, index) => (
                    <div
                      className="bg-gray-400 animate-pulse rounded-full aspect-square w-30"
                      key={index}
                    ></div>
                  ))}
                </div>
              ) : (
                <div className="profile-avatar-grid w-full grid grid-cols-5 gap-4">
                  {profileAvatarQuery.data.data.map((item, index) => (
                    <div
                      key={index}
                      className="rounded-full aspect-square w-30"
                    >
                      <img
                        src={item.fields.avatarUrl}
                        alt={`Avatar ${index}`}
                        className={` aspect-square rounded-full w-40 cursor-pointer transition-all ${
                          formData.profilephotos === item.fields.avatarUrl
                            ? "ring-4 ring-blue-500 scale-110"
                            : "hover:scale-105"
                        }`}
                        onClick={() =>
                          handleAvatarSelect(item.fields.avatarUrl)
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="profile-avatar-modal-actions mt-4 flex justify-center">
              <button
                onClick={updateAvatar}
                disabled={updateUserMutation.isPending}
                className="bg-blue-600 text-white px-6 py-2 rounded-full relative"
              >
                {updateUserMutation.isPending ? (
                  <>
                    <span className="opacity-0">Update Avatar</span>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  </>
                ) : (
                  "Update Avatar"
                )}
              </button>
              <button
                onClick={closeAvatarModal}
                className="bg-gray-600 text-white px-6 py-2 rounded-full ml-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileEdit;
